/* eslint-disable react-hooks/exhaustive-deps */
// @vendors
import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';

// @services
import empresaService from '../services/empresaService';
import usuarioService from '../services/usuarioService';

// @components
import ConfirmModal from '../components/modals/ConfirmModal';

const Empresas = () => {
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentEmpresa, setCurrentEmpresa] = useState({ nombre: '', contacto: '', estado: 'Activo' });
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('sinAprobar'); // 'sinAprobar' o 'aprobadas'
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [companySelected, setCompanySelected] = useState(null);
  const [isAprobar, setIsAprobar] = useState(false);

  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEmpresas, setTotalEmpresas] = useState(0);
  const pageSize = 20;

  // Estados para filtros
  const [filtros, setFiltros] = useState({
    categoria: '',
    ciudad: '',
    search: '',
    minYearsExperience: ''
  });
  const [categorias, setCategorias] = useState([]);
  const [ciudades, setCiudades] = useState([]);

  // Memorizar las dependencias para evitar cambios de tamaño en useEffect
  const filtrosDependencias = useMemo(() => {
    return JSON.stringify(filtros);
  }, [filtros]);

  // Cargar empresas al montar el componente y cuando cambie la página, filtros o pestaña activa
  useEffect(() => {
    fetchEmpresas();
  }, [currentPage, filtrosDependencias, activeTab]);

  // Cargar datos de filtros al montar el componente
  useEffect(() => {
    //fetchFiltersData();
  }, []);

  // Función para obtener datos de filtros (categorías y ciudades)
  const fetchFiltersData = async () => {
    try {
      const filtersData = await usuarioService.getCompanyFiltersData();
      setCategorias(filtersData.categorias || []);
      setCiudades(filtersData.ciudades || []);
      console.log('Categorías disponibles:', filtersData.categorias);
      console.log('Ciudades disponibles:', filtersData.ciudades);
    } catch (error) {
      console.error('Error al cargar datos de filtros:', error);
      // El servicio ya maneja el fallback internamente
      setCategorias([]);
      setCiudades([]);
    }
  };

  // Función para obtener empresas con paginación y filtros
  const fetchEmpresas = async () => {
    try {
      setLoading(true);

      // Preparar filtros para el backend, incluyendo el estado según la pestaña activa
      const backendFilters = {
        ...filtros,
        estado: activeTab === 'sinAprobar' ? 'pending' : activeTab === 'pendingToApprove' ? 'pendingAdmin' : 'active'
      };

      // Usar el nuevo endpoint optimizado con paginación y filtros
      const response = await usuarioService.getCompanies(currentPage, pageSize, backendFilters);

      // El endpoint ya devuelve solo empresas, no necesitamos filtrar
      const empresasData = response.data || response.companies || response.users || [];

      // Actualizar información de paginación - revisar diferentes estructuras posibles
      const totalPagesFromResponse = response.totalPages || response.pagination?.totalPages || Math.ceil((response.total || response.pagination?.total || 0) / pageSize);
      const totalEmpresasFromResponse = response.total || response.pagination?.total || response.count || 0;

      setTotalPages(totalPagesFromResponse);
      setTotalEmpresas(totalEmpresasFromResponse);

      // Transformar los datos para adaptarlos a nuestra estructura
      const empresasFormateadas = empresasData.map(empresa => {
        // Normalizar el estado para que siempre usemos valores consistentes
        let estado = empresa.state || empresa.status || empresa.companyStatus || 'pending';

        // Convertir posibles variaciones de estado a nuestros valores estandarizados
        if (estado.toLowerCase() === 'active' || estado === 'Activo') estado = 'Activo';
        if (estado.toLowerCase() === 'inactive' || estado === 'Inactivo') estado = 'Inactive';
        if (estado.toLowerCase() === 'pending' || estado === 'Pendiente') estado = 'pending';
        if (estado.toLowerCase() === 'pendingAdmin') estado = 'pendingToApprove';

        return {
          id: empresa.id || '',
          nombre: empresa.name || empresa.companyName || '',
          telefono: empresa.phone || empresa.phoneNumber || '',
          email: empresa.email || empresa.contactEmail || '',
          categoria: empresa.category || empresa.businessCategory || '',
          documentos: empresa.documents || [],
          ciudades: empresa.locations || empresa.cities || empresa.serviceCities || [],
          imagenPerfil: empresa.profilePictureUrl || empresa.profileImage || '',
          estado: estado,
          // Una empresa está aprobada si su estado es 'Activo'
          aprobada: (estado === 'Activo')
        };
      });

      setEmpresas(empresasFormateadas);
      setError(null);
      toast.success("Empresas cargadas correctamente");
    } catch (err) {
      setError('No se pudieron cargar las empresas. Por favor, intenta de nuevo más tarde.');
      toast.error('Error al cargar empresas');
    } finally {
      setLoading(false);
    }
  };

  // Función para abrir el modal en modo creación
  const handleAddEmpresa = () => {
    setCurrentEmpresa({ nombre: '', contacto: '', estado: 'Activo' });
    setIsEditing(false);
    setModalOpen(true);
  };

  // Función para guardar una empresa (crear o actualizar)
  const handleSaveEmpresa = async () => {
    try {
      if (isEditing) {
        await empresaService.updateEmpresa(currentEmpresa.id, currentEmpresa);
      } else {
        await empresaService.createEmpresa(currentEmpresa);
      }
      setModalOpen(false);
      fetchEmpresas(); // Recargar la lista después de guardar
    } catch (err) {
      toast.error('Error al guardar la empresa. Por favor, intenta de nuevo.');
    }
  };

  const handleCancelSubscription = async (id) => {
    try {
      const response = await usuarioService.cancelSubscription(id);
      toast.success('Suscripción cancelada correctamente');
      return response;
    } catch (error) {
      const message = error.data.message || 'Error al cancelar la suscripción';
      toast.error(message);
      return error;
    }
  }

  // Función para eliminar una empresa
  const handleDeleteEmpresa = async () => {
    try {
      setLoading(true);
      if (activeTab !== 'sinAprobar') {
        const response = await handleCancelSubscription(companySelected);
        if (response.status > 399) return;
      }

      await usuarioService.deleteUsuario(companySelected);
      const nuevasEmpresas = empresas.filter(empresa => empresa.id !== companySelected);
      setEmpresas(nuevasEmpresas);
      toast.success('Empresa eliminada exitosamente');
    } catch (err) {
      toast.error('Error al eliminar empresa');
    } finally {
      setLoading(false);
    }
  };

  // Función para manejar cambios en el formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentEmpresa({ ...currentEmpresa, [name]: value });
  };

  // El backend ya maneja todos los filtros, no necesitamos filtrado del cliente
  const empresasFiltradas = empresas;

  // Función para cambiar entre pestañas
  const cambiarTab = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1); // Resetear a la primera página al cambiar de pestaña
  };

  // Funciones de navegación de paginación
  const goToFirstPage = () => {
    setCurrentPage(1);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToLastPage = () => {
    setCurrentPage(totalPages);
  };

  // Función para manejar cambios en los filtros
  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
    setCurrentPage(1); // Resetear a la primera página al cambiar filtros
  };

  // Función para limpiar filtros
  const limpiarFiltros = () => {
    setFiltros({
      categoria: '',
      ciudad: '',
      search: '',
      minYearsExperience: ''
    });
    setCurrentPage(1);
  };

  // Función para aprobar una empresa
  const handleAprobarEmpresa = async () => {
    try {
      setLoading(true);
      await empresaService.changeCompanyState(companySelected, 'active');

      const nuevasEmpresas = empresas.filter(empresa => empresa.id !== companySelected);
      setEmpresas(nuevasEmpresas);
      toast.success('Empresa aprobada exitosamente');
    } catch (err) {
      toast.error('Error al aprobar la empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenConfirmModal = (id, isAprobar) => {
    setCompanySelected(id);
    setIsAprobar(isAprobar);
    setShowConfirmModal(true);
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">Gestión de Empresas</h2>
      <div className="bg-white rounded-lg shadow-md p-6">
        <p className="text-gray-600 mb-4">
          Aquí podrás administrar todas las empresas registradas en el sistema.
        </p>
        <div className="mt-6">
          <button
            onClick={handleAddEmpresa}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mr-2"
          >
            Agregar Empresa
          </button>
          <button className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded">
            Exportar Datos
          </button>
        </div>

        {/* Pestañas */}
        <div className="mt-6 border-b border-gray-200">
          <ul className="flex flex-wrap -mb-px">
            <li className="mr-2">
              <button
                onClick={() => cambiarTab('sinAprobar')}
                className={`inline-block py-2 px-4 text-sm font-medium ${activeTab === 'sinAprobar'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300'}`}
              >
                Pendientes por suscripción
              </button>
            </li>
            <li className="mr-2">
              <button
                onClick={() => cambiarTab('pendingToApprove')}
                className={`inline-block py-2 px-4 text-sm font-medium ${activeTab === 'pendingToApprove'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300'}`}
              >
                Pendientes por aprobación
              </button>
            </li>
            <li className="mr-2">
              <button
                onClick={() => cambiarTab('aprobadas')}
                className={`inline-block py-2 px-4 text-sm font-medium ${activeTab === 'aprobadas'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300'}`}
              >
                Aprobadas
              </button>
            </li>
          </ul>
        </div>

        {/* Filtros */}
        <div className="mt-6 bg-gray-50 p-4 rounded-lg">
          <h4 className="text-lg font-medium text-gray-700 mb-4">Filtros</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Filtro de búsqueda */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buscar empresa
              </label>
              <input
                type="text"
                placeholder="Nombre, apellido o descripción..."
                value={filtros.search}
                onChange={(e) => handleFiltroChange('search', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Filtro por categoría */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categoría
              </label>
              <select
                value={filtros.categoria}
                onChange={(e) => handleFiltroChange('categoria', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todas las categorías</option>
                {categorias.map((categoria, index) => (
                  <option key={index} value={categoria}>
                    {categoria}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtro por ciudad */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ciudad
              </label>
              <input
                type="text"
                placeholder="Buscar ciudad..."
                value={filtros.ciudad}
                onChange={(e) => handleFiltroChange('ciudad', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Filtro por años de experiencia */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Años de experiencia mínima
              </label>
              <input
                type="number"
                min="0"
                placeholder="Ej: 5"
                value={filtros.minYearsExperience}
                onChange={(e) => handleFiltroChange('minYearsExperience', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Botón para limpiar filtros */}
          <div className="mt-4">
            <button
              onClick={limpiarFiltros}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm"
            >
              Limpiar filtros
            </button>
          </div>
        </div>

        {/* Estado de carga y error */}
        {loading && <p className="mt-4 text-gray-600">Cargando empresas...</p>}
        {error && <p className="mt-4 text-red-500">{error}</p>}

        {/* Tabla de empresas */}
        {!loading && !error && (
          <div className="mt-4 overflow-x-auto" style={{ minHeight: '500px' }}>
            {empresasFiltradas.length === 0 ? (
              <p className="text-gray-500 py-4">No hay empresas {activeTab === 'sinAprobar' ? 'sin aprobar' : 'aprobadas'} que coincidan con los filtros aplicados.</p>
            ) : (
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="bg-gray-100 text-gray-600 uppercase text-sm leading-normal">
                    <th className="py-3 px-6 text-left">Imagen</th>
                    <th className="py-3 px-6 text-left">Nombre</th>
                    <th className="py-3 px-6 text-left">Teléfono</th>
                    <th className="py-3 px-6 text-left">Email</th>
                    <th className="py-3 px-6 text-left">Categoría</th>
                    <th className="py-3 px-6 text-left">Documentos</th>
                    <th className="py-3 px-6 text-left">Ciudades</th>
                    <th className="py-3 px-6 text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody className="text-gray-600 text-sm">
                  {empresasFiltradas.map(empresa => (
                    <tr key={empresa.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-3 px-6 text-left">
                        {empresa.imagenPerfil ? (
                          <img
                            src={empresa.imagenPerfil}
                            alt={`${empresa.nombre} logo`}
                            className="h-10 w-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <span className="text-gray-500 text-xs">{empresa.nombre.charAt(0)}</span>
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-6 text-left">{empresa.nombre}</td>
                      <td className="py-3 px-6 text-left">{empresa.telefono}</td>
                      <td className="py-3 px-6 text-left">{empresa.email}</td>
                      <td className="py-3 px-6 text-left">
                        <span className="bg-blue-100 text-blue-700 py-1 px-3 rounded-full text-xs">
                          {empresa.categoria || 'Sin categoría'}
                        </span>
                      </td>
                      <td className="py-3 px-6 text-left">
                        <div className="flex space-x-2">
                          {/* Mostramos hasta 3 documentos como iconos */}
                          {Array.isArray(empresa.documentos) ? (
                            empresa.documentos.length > 0 ? (
                              // Si hay documentos, mostramos iconos para cada uno (hasta 3)
                              empresa.documentos.slice(0, 3).map((doc, index) => (
                                <a
                                  key={index}
                                  href={doc.url || doc.fileUrl || doc.documentUrl || '#'}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center cursor-pointer"
                                  title={doc.name || doc.tipo || `Documento ${index + 1}`}
                                  onClick={(e) => {
                                    // Si no hay URL, prevenir la navegación
                                    if (!doc.url && !doc.fileUrl && !doc.documentUrl) {
                                      e.preventDefault();
                                      alert(`No hay URL disponible para el documento: ${doc.name || doc.tipo || `Documento ${index + 1}`}`);
                                    }
                                  }}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                </a>
                              ))
                            ) : (
                              // Si no hay documentos
                              <span className="text-xs text-gray-400">Sin documentos</span>
                            )
                          ) : (
                            <span className="text-xs text-gray-400">Sin documentos</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-2 text-center">
                        {Array.isArray(empresa.ciudades) && empresa.ciudades.length > 0 ? (
                          <div className="relative inline-block text-center">
                            <div>
                              <button type="button"
                                className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
                                id={`dropdown-button-${empresa.id}`}
                                onClick={() => {
                                  const dropdown = document.getElementById(`dropdown-menu-${empresa.id}`);
                                  if (dropdown) {
                                    dropdown.classList.toggle('hidden');
                                  }
                                }}
                              >
                                {empresa.ciudades.length}
                                <svg className="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </button>
                            </div>
                            <div className="hidden origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10" id={`dropdown-menu-${empresa.id}`}>
                              <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby={`dropdown-button-${empresa.id}`}>
                                {empresa.ciudades.map((ciudad, index) => (
                                  <div key={index} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                    {typeof ciudad === 'string'
                                      ? ciudad
                                      : ciudad.name || ciudad.nombre || ciudad.city || ciudad.cityName || 'Ciudad'}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">Sin ciudades</span>
                        )}
                      </td>
                      <td className="py-3 px-6 text-center">
                        <div className="flex item-center justify-center">
                          {activeTab === 'pendingToApprove' && (
                            <button
                              //onClick={() => handleAprobarEmpresa(empresa.id)}
                              onClick={() => handleOpenConfirmModal(empresa.id, true)}
                              className="text-green-500 hover:text-green-700 mx-1"
                            >
                              Aprobar
                            </button>
                          )}
                          <button
                            onClick={() => handleOpenConfirmModal(empresa.id, false)}
                            className="text-red-500 hover:text-red-700 mx-1"
                          >
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Controles de paginación */}
        {!loading && !error && totalPages > 0 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Mostrando página {currentPage} de {totalPages} ({totalEmpresas} empresas en total)
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={goToFirstPage}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded ${currentPage === 1
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
              >
                ««
              </button>
              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded ${currentPage === 1
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
              >
                ‹ Anterior
              </button>
              <span className="px-3 py-1 bg-gray-100 rounded">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded ${currentPage === totalPages
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
              >
                Siguiente ›
              </button>
              <button
                onClick={goToLastPage}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded ${currentPage === totalPages
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
              >
                »»
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal para crear/editar empresa */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">
              {isEditing ? 'Editar Empresa' : 'Agregar Nueva Empresa'}
            </h3>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="nombre">
                Nombre
              </label>
              <input
                type="text"
                id="nombre"
                name="nombre"
                value={currentEmpresa.nombre}
                onChange={handleInputChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="contacto">
                Contacto
              </label>
              <input
                type="text"
                id="contacto"
                name="contacto"
                value={currentEmpresa.contacto}
                onChange={handleInputChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="estado">
                Estado
              </label>
              <select
                id="estado"
                name="estado"
                value={currentEmpresa.estado}
                onChange={handleInputChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              >
                <option value="Activo">Activo</option>
                <option value="Inactivo">Inactivo</option>
                <option value="Pendiente">Pendiente</option>
              </select>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setModalOpen(false)}
                className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded mr-2"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveEmpresa}
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={isAprobar ? handleAprobarEmpresa : handleDeleteEmpresa}
        title={isAprobar ? "Aprobar Empresa" : "Eliminar Empresa"}
        message={isAprobar ? "¿Estás seguro de que deseas aprobar esta empresa?" : "¿Estás seguro de que deseas eliminar esta empresa?"}
        confirmText={isAprobar ? "Aprobar" : "Eliminar"}
        cancelText="Cancelar"
        confirmButtonColor={isAprobar ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}
      />
    </div>
  );
};

export default Empresas;
